/**
 * Standings calculation utilities for round robin stages
 */

import type { PlayerStanding, StageStandings, GameScore } from '@/draw/types';
import type { StandingPointsBreakdown } from '@/lib/futsal-standings';
import { playerModelV2 } from '@/models/playerModelV2';
import { tryResolveAppState } from '@/lib/scorecard/resolve';
import { resolveParticipantLabel } from '@/app/[locale]/event/[eventId]/libs/participantLabel';
import {
  isCompletedGame,
  isUnfinishedLastGame,
  retiredPlayedCounts,
  retirementCredit,
} from '@/app/[locale]/match/libs/retirement';

// Database types for input
type DatabasePlayer = {
  id: string;
  first_name: string;
  last_name: string;
  /** A trimmed, non-empty alias is called by it everywhere, above the personal name (product ruling 2026-09-21). */
  alias?: string | null;
};

type DatabaseMatch = {
  id: string;
  draw_match_id?: string | null;
  round_id?: string | null;
  player_1?: DatabasePlayer | null;
  player_2?: DatabasePlayer | null;
  winner?: DatabasePlayer | null;
  outcome?: string | null;
  status: string;
  score?: unknown; // jsonb from database
  is_bye?: boolean | null;
  event_match_schedule?: {
    match?: {
      standing_points?: unknown;
      /** The format played; authoritative for a retirement's credit. */
      best_of?: number | null;
      /** The rest of the format, to tell a retirement's unfinished game in a score array. */
      points_per_game?: number | null;
      deciding_game_points?: number | null;
      deuce_rule?: string | null;
      /** Set when a player retired: the single source of the 'retired' outcome. */
      retired_player_id?: string | null;
      /** Completed games won by each side (the partial game never counts). */
      player_1_games?: number | null;
      player_2_games?: number | null;
      match_game?: Array<{
        id: string;
        game_number: number;
        player_1_score: number;
        player_2_score: number;
        /** 'in_progress' marks a retired match's unfinished game; absent reads as completed. */
        status?: string | null;
      }> | null;
    } | null;
  } | null;
};

type DatabaseStage = {
  id: string;
  stage_id: string;
  name: string;
  type: string;
  context?: string | null;
};

type DatabaseRound = {
  id: string;
  stage_id: string | null;
  round_id?: string | null;
  name: string;
  round_number: number;
};

/**
 * Calculate standings for a single round robin stage
 */
export function calculateRoundRobinStandings(params: {
  stage: DatabaseStage;
  rounds: DatabaseRound[];
  matches: DatabaseMatch[];
  scoringSystem?: 'standard' | 'games' | 'match_points' | 'futsal_show';
  tiebreakerOrder?: ('games_ratio' | 'points_ratio')[];
  isJapanese?: boolean;
}): StageStandings {
  const {
    stage,
    rounds,
    matches,
    scoringSystem = 'standard',
    tiebreakerOrder = ['games_ratio', 'points_ratio'],
    isJapanese = false,
  } = params;

  // Get all rounds for this stage
  const stageRounds = rounds.filter((round) => round.stage_id === stage.id);

  // Get all round IDs for this stage
  const stageRoundIds = new Set(stageRounds.map((round) => round.id));

  // Filter matches to only include those that belong to this stage's rounds
  const allStageMatches = matches.filter(
    (match) => match.round_id && stageRoundIds.has(match.round_id),
  );

  // Initialize player stats
  const playerStats = new Map<
    string,
    {
      playerId: string;
      playerName: string;
      wins: number;
      losses: number;
      gamesWon: number;
      gamesLost: number;
      gamePointsFor: number;
      gamePointsAgainst: number;
      headToHead: Record<string, 'win' | 'loss'>;
    }
  >();

  // Track match results for match_points scoring system
  const matchResults = new Map<
    string,
    Array<{
      gamesWon: number;
      gamesLost: number;
    }>
  >();

  // Collect all unique players from matches (excluding BYE players)
  const allPlayers = new Set<string>();
  allStageMatches.forEach((match) => {
    // Add player_1 if it exists and is not a BYE player
    if (
      match.player_1 &&
      match.player_1.first_name !== 'BYE' &&
      !match.player_1.id.startsWith('bye-')
    ) {
      allPlayers.add(match.player_1.id);
    }
    // Add player_2 if it exists and is not a BYE player
    if (
      match.player_2 &&
      match.player_2.first_name !== 'BYE' &&
      !match.player_2.id.startsWith('bye-')
    ) {
      allPlayers.add(match.player_2.id);
    }
  });

  // Initialize player stats
  allPlayers.forEach((playerId) => {
    const player = findPlayerInMatches(allStageMatches, playerId);
    if (player) {
      playerStats.set(playerId, {
        playerId,
        // Alias beats the personal name (product ruling 2026-09-21); a team
        // division's row is then relabelled to the team name downstream by
        // `applyTeamLabelsToStandings`, which keeps team above alias.
        playerName: resolveParticipantLabel({
          entry: null,
          player,
          isJapanese,
        }),
        wins: 0,
        losses: 0,
        gamesWon: 0,
        gamesLost: 0,
        gamePointsFor: 0,
        gamePointsAgainst: 0,
        headToHead: {},
      });
    }
  });

  // Futsal-specific tracking (only used when scoringSystem is 'futsal_show')
  const futsalPlayerData = new Map<
    string,
    {
      draws: number;
      goalsFor: number;
      goalsAgainst: number;
      matchBreakdowns: Array<{
        matchLabel: string;
        opponentId: string;
        opponentName: string;
        score: string;
        points: number;
        items: Array<{
          ruleKey: string;
          reasonKey: string;
          reasonParams?: Record<string, string | number>;
          points: number;
        }>;
      }>;
    }
  >();

  if (scoringSystem === 'futsal_show') {
    allPlayers.forEach((playerId) => {
      futsalPlayerData.set(playerId, {
        draws: 0,
        goalsFor: 0,
        goalsAgainst: 0,
        matchBreakdowns: [],
      });
    });
  }

  // Process all completed matches to calculate stats
  allStageMatches.forEach((match) => {
    if (match.outcome === 'not_played') return; // void: excluded from every tally

    if (match.status === 'completed' && !match.winner && match.outcome === 'dual_withdrawal') {
      // Loss for BOTH players (identical to a single withdrawal's loser credit, applied to both).
      for (const p of [match.player_1, match.player_2]) {
        const stats = p && playerStats.get(p.id);
        if (!stats) continue;
        stats.losses++;
        stats.gamesLost += 3; // the walkover loser's standard 0-3 credit, applied to both

        if (!matchResults.has(stats.playerId)) matchResults.set(stats.playerId, []);
        matchResults.get(stats.playerId)!.push({ gamesWon: 0, gamesLost: 3 });
      }
      return;
    }

    if (match.status === 'completed' && match.winner) {
      // Skip if this is a BYE match (winner is a BYE player)
      if (match.winner.first_name === 'BYE' || match.winner.id.startsWith('bye-')) {
        return;
      }
      const winnerStats = playerStats.get(match.winner.id);
      const loser = match.winner.id === match.player_1?.id ? match.player_2 : match.player_1;

      // Skip if loser is a BYE player
      if (loser && (loser.first_name === 'BYE' || loser.id.startsWith('bye-'))) {
        return;
      }

      const loserStats = loser ? playerStats.get(loser.id) : null;

      if (winnerStats && loserStats) {
        // Update wins/losses
        winnerStats.wins++;
        loserStats.losses++;

        // Parse and process game scores (only if available). Points count every game
        // played, a retirement's unfinished game included.
        const isWinnerPlayer1 = match.winner.id === match.player_1?.id;
        const played = parsePlayedGameScores(match);
        const retiredCredit = retirementGameCredit(match, played, isWinnerPlayer1);
        if (played.length > 0 || retiredCredit) {
          let winnerGames = 0;
          let loserGames = 0;

          played.forEach((game) => {
            const winnerScore = isWinnerPlayer1 ? game.player1Score : game.player2Score;
            const loserScore = isWinnerPlayer1 ? game.player2Score : game.player1Score;

            if (game.completed) {
              if (winnerScore > loserScore) {
                winnerGames++;
              } else {
                loserGames++;
              }
            }

            // Update point stats
            winnerStats.gamePointsFor += winnerScore;
            winnerStats.gamePointsAgainst += loserScore;
            loserStats.gamePointsFor += loserScore;
            loserStats.gamePointsAgainst += winnerScore;
          });

          // A retirement credits the winner the full match; the retiree keeps the
          // completed games they won.
          if (retiredCredit) {
            winnerGames = retiredCredit.winnerGames;
            loserGames = retiredCredit.loserGames;
          }

          // Update game stats
          winnerStats.gamesWon += winnerGames;
          winnerStats.gamesLost += loserGames;
          loserStats.gamesWon += loserGames;
          loserStats.gamesLost += winnerGames;

          // Store match results for match_points scoring
          if (!matchResults.has(winnerStats.playerId)) {
            matchResults.set(winnerStats.playerId, []);
          }
          if (!matchResults.has(loserStats.playerId)) {
            matchResults.set(loserStats.playerId, []);
          }
          matchResults
            .get(winnerStats.playerId)!
            .push({ gamesWon: winnerGames, gamesLost: loserGames });
          matchResults
            .get(loserStats.playerId)!
            .push({ gamesWon: loserGames, gamesLost: winnerGames });
        } else {
          // For walkover matches without scores, assume standard squash result (3-0)
          // This gives the winner 3 games and loser 0 games
          winnerStats.gamesWon += 3;
          loserStats.gamesLost += 3;

          // Store match results for match_points scoring
          if (!matchResults.has(winnerStats.playerId)) matchResults.set(winnerStats.playerId, []);
          if (!matchResults.has(loserStats.playerId)) matchResults.set(loserStats.playerId, []);
          matchResults.get(winnerStats.playerId)!.push({ gamesWon: 3, gamesLost: 0 });
          matchResults.get(loserStats.playerId)!.push({ gamesWon: 0, gamesLost: 3 });
        }
      }
    }
  });

  // Process futsal standing points from cached standing_points on match
  if (scoringSystem === 'futsal_show') {
    allStageMatches.forEach((match) => {
      if (match.status !== 'completed') return;
      if (!match.player_1 || !match.player_2) return;
      if (match.player_1.first_name === 'BYE' || match.player_2.first_name === 'BYE') return;

      const p1Data = futsalPlayerData.get(match.player_1.id);
      const p2Data = futsalPlayerData.get(match.player_2.id);

      if (match.outcome === 'dual_withdrawal') {
        // Synthetic loss point for both — no standing_points breakdown exists for a withdrawal.
        const withdrawalItems = [
          {
            ruleKey: 'standingRules.loss',
            reasonKey: 'standingRules.reasonMostGoals',
            reasonParams: { winner: 0, loser: 0 },
            points: 1, // calculateStandingPoints' loss value; a withdrawal is a loss, not a void
          },
        ];
        if (p1Data) {
          p1Data.matchBreakdowns.push({
            matchLabel: match.draw_match_id || match.id,
            opponentId: match.player_2.id,
            opponentName: playerModelV2.formatName(
              match.player_2.first_name,
              match.player_2.last_name,
              null,
              { isJapanese },
            ),
            score: '0-0',
            points: 1,
            items: withdrawalItems,
          });
        }
        if (p2Data) {
          p2Data.matchBreakdowns.push({
            matchLabel: match.draw_match_id || match.id,
            opponentId: match.player_1.id,
            opponentName: playerModelV2.formatName(
              match.player_1.first_name,
              match.player_1.last_name,
              null,
              { isJapanese },
            ),
            score: '0-0',
            points: 1,
            items: withdrawalItems,
          });
        }
        return;
      }
      if (match.outcome === 'not_played') return; // void: excluded from every tally

      const breakdown = match.event_match_schedule?.match
        ?.standing_points as StandingPointsBreakdown | null;
      if (!breakdown) return;

      // Get scores from match_game (futsal has 1 game per match)
      const games = match.event_match_schedule?.match?.match_game;
      const homeScore = games?.[0]?.player_1_score ?? 0;
      const awayScore = games?.[0]?.player_2_score ?? 0;
      const scoreStr = `${homeScore}-${awayScore}`;

      if (p1Data) {
        p1Data.goalsFor += homeScore;
        p1Data.goalsAgainst += awayScore;
        if (homeScore === awayScore) p1Data.draws++;
        p1Data.matchBreakdowns.push({
          matchLabel: match.draw_match_id || match.id,
          opponentId: match.player_2.id,
          opponentName: playerModelV2.formatName(
            match.player_2.first_name,
            match.player_2.last_name,
            null,
            { isJapanese },
          ),
          score: scoreStr,
          points: breakdown.home.total,
          items: breakdown.home.items,
        });
      }

      if (p2Data) {
        p2Data.goalsFor += awayScore;
        p2Data.goalsAgainst += homeScore;
        if (homeScore === awayScore) p2Data.draws++;
        p2Data.matchBreakdowns.push({
          matchLabel: match.draw_match_id || match.id,
          opponentId: match.player_1.id,
          opponentName: playerModelV2.formatName(
            match.player_1.first_name,
            match.player_1.last_name,
            null,
            { isJapanese },
          ),
          score: `${awayScore}-${homeScore}`,
          points: breakdown.away.total,
          items: breakdown.away.items,
        });
      }
    });
  }

  // Convert to standings array and calculate points/percentages
  // Calculate match points mapping based on match format (best-of-1, best-of-3, or best-of-5)
  // Best-of-1: 1-0=1, 0-1=0
  // Best-of-3: 2-0=3, 2-1=2, 1-2=1, 0-2=0
  // Best-of-5: 3-0=5, 3-1=4, 3-2=3, 2-3=2, 1-3=1, 0-3=0
  const getMatchPoints = (gamesWon: number, gamesLost: number): number => {
    // Infer match format from game scores
    // Best-of-1: max games won is 1 (1-0 or 0-1)
    // Best-of-3: max games won is 2 (2-0 or 2-1)
    // Best-of-5: max games won is 3 (3-0, 3-1, or 3-2)
    const isBestOf1 = gamesWon <= 1 && gamesLost <= 1;
    const isBestOf3 = !isBestOf1 && gamesWon <= 2 && gamesLost <= 2;

    if (isBestOf1) {
      // Best-of-1 scoring
      if (gamesWon === 1 && gamesLost === 0) return 1; // Win
      if (gamesWon === 0 && gamesLost === 1) return 0; // Loss
      // Fallback for incomplete matches or edge cases
      return 0;
    } else if (isBestOf3) {
      // Best-of-3 scoring
      if (gamesWon === 2 && gamesLost === 0) return 3; // Clean win
      if (gamesWon === 2 && gamesLost === 1) return 2; // Close win
      if (gamesWon === 1 && gamesLost === 2) return 1; // Close loss
      if (gamesWon === 0 && gamesLost === 2) return 0; // Clean loss
      // Fallback for incomplete matches or edge cases
      return 0;
    } else {
      // Best-of-5 scoring
      if (gamesWon === 3 && gamesLost === 0) return 5;
      if (gamesWon === 3 && gamesLost === 1) return 4;
      if (gamesWon === 3 && gamesLost === 2) return 3;
      if (gamesWon === 2 && gamesLost === 3) return 2;
      if (gamesWon === 1 && gamesLost === 3) return 1;
      if (gamesWon === 0 && gamesLost === 3) return 0;
      // Fallback for incomplete matches or edge cases
      return 0;
    }
  };

  const standings: PlayerStanding[] = Array.from(playerStats.values()).map((stats) => {
    const totalPoints = stats.gamePointsFor + stats.gamePointsAgainst;

    // Calculate points based on scoring system
    let points: number;
    switch (scoringSystem) {
      case 'games':
        points = stats.gamesWon; // 1 point per game won
        break;
      case 'match_points':
        // Sum match points from all matches
        const playerMatches = matchResults.get(stats.playerId) || [];
        points = playerMatches.reduce((sum, matchResult) => {
          const matchPoints = getMatchPoints(matchResult.gamesWon, matchResult.gamesLost);
          return sum + matchPoints;
        }, 0);
        break;
      case 'futsal_show': {
        const fData = futsalPlayerData.get(stats.playerId);
        points = fData ? fData.matchBreakdowns.reduce((sum, mb) => sum + mb.points, 0) : 0;
        break;
      }
      case 'standard':
      default:
        points = stats.wins * 2; // 2 points per win, 0 for loss
        break;
    }

    return {
      playerId: stats.playerId,
      playerName: stats.playerName,
      position: 0, // Will be set after sorting
      wins: stats.wins,
      losses: stats.losses,
      points: points,
      gamesWon: stats.gamesWon,
      gamesLost: stats.gamesLost,
      gamePointsFor: stats.gamePointsFor,
      gamePointsAgainst: stats.gamePointsAgainst,
      gamePointsPercentage: totalPoints > 0 ? (stats.gamePointsFor / totalPoints) * 100 : 0,
      headToHead: stats.headToHead,
      // Futsal fields
      ...(scoringSystem === 'futsal_show' &&
        (() => {
          const fData = futsalPlayerData.get(stats.playerId);
          return fData
            ? {
                draws: fData.draws,
                goalsFor: fData.goalsFor,
                goalsAgainst: fData.goalsAgainst,
                goalDifference: fData.goalsFor - fData.goalsAgainst,
                matchBreakdowns: fData.matchBreakdowns,
              }
            : {};
        })()),
    };
  });

  // Sort standings and set positions
  sortStandings(standings, tiebreakerOrder);

  return {
    stageId: stage.stage_id,
    stageName: stage.name,
    standings,
    lastUpdated: new Date(),
  };
}

/** What a round-robin stage is ranked by; a pool and a round-robin main draw may differ. */
export type RoundRobinScoring = {
  scoringSystem?: 'standard' | 'games' | 'match_points' | 'futsal_show';
  tiebreakerOrder?: ('games_ratio' | 'points_ratio')[];
};

/**
 * Calculate standings for all round robin stages in a draw
 */
export function calculateStandingsForDraw(params: {
  stages: DatabaseStage[];
  rounds: DatabaseRound[];
  matches: DatabaseMatch[];
  scoringSystem?: RoundRobinScoring['scoringSystem'];
  tiebreakerOrder?: RoundRobinScoring['tiebreakerOrder'];
  isJapanese?: boolean;
  /** Per-stage scoring; a field it leaves undefined falls back to the draw-wide one. */
  settingsForStage?: (stage: DatabaseStage) => RoundRobinScoring | undefined;
}): StageStandings[] {
  const { stages, rounds, matches, scoringSystem, tiebreakerOrder, isJapanese, settingsForStage } =
    params;

  const roundRobinStages = stages.filter((stage) => stage.type === 'round_robin');

  return roundRobinStages.map((stage) => {
    const perStage = settingsForStage?.(stage);
    return calculateRoundRobinStandings({
      stage,
      rounds,
      matches,
      scoringSystem: perStage?.scoringSystem ?? scoringSystem,
      tiebreakerOrder: perStage?.tiebreakerOrder ?? tiebreakerOrder,
      isJapanese,
    });
  });
}

// Helper functions

function findPlayerInMatches(matches: DatabaseMatch[], playerId: string): DatabasePlayer | null {
  for (const match of matches) {
    if (match.player_1?.id === playerId) return match.player_1;
    if (match.player_2?.id === playerId) return match.player_2;
  }
  return null;
}

/** A played game: `completed` is false only for a retired match's unfinished game. */
export type PlayedGameScore = GameScore & { completed: boolean };

/** Whether a player retired: `match.retired_player_id` decides; the draw outcome mirrors it. */
function isRetiredMatch(match: DatabaseMatch): boolean {
  return !!match.event_match_schedule?.match?.retired_player_id || match.outcome === 'retired';
}

/**
 * The credited game counts (winner first) for a match a retirement decided, null otherwise.
 * The played counts are the match row's `player_*_games` when recorded, else the completed
 * games in `played`; the format is the match row's `best_of`.
 */
function retirementGameCredit(
  match: DatabaseMatch,
  played: PlayedGameScore[],
  isWinnerPlayer1: boolean,
): { winnerGames: number; loserGames: number } | null {
  if (!isRetiredMatch(match)) return null;
  const row = match.event_match_schedule?.match;
  const counts = retiredPlayedCounts(
    row,
    played.map((g) => ({
      player1: g.player1Score,
      player2: g.player2Score,
      completed: g.completed,
    })),
  );
  const credit = retirementCredit(counts, isWinnerPlayer1 ? 'player2' : 'player1', row?.best_of);
  return isWinnerPlayer1
    ? { winnerGames: credit.player1, loserGames: credit.player2 }
    : { winnerGames: credit.player2, loserGames: credit.player1 };
}

/**
 * Every game played, in order, a retired match's unfinished game included (`completed: false`):
 * for points and for showing the games actually played. Count games with `parseGameScores`.
 */
export function parsePlayedGameScores(match: DatabaseMatch): PlayedGameScore[] {
  // First, try to get game scores from match_game table (more detailed)
  if (match.event_match_schedule?.match?.match_game) {
    return [...match.event_match_schedule.match.match_game]
      .sort((a, b) => a.game_number - b.game_number)
      .map((game) => ({
        game: game.game_number,
        player1Score: game.player_1_score,
        player2Score: game.player_2_score,
        completed: isCompletedGame(game),
      }));
  }

  // Fallback to draw_match.score field (JSON)
  const scoreData = match.score;
  if (!scoreData || typeof scoreData !== 'object') return [];

  try {
    // Handle different possible formats from database
    if (Array.isArray(scoreData)) {
      const games: PlayedGameScore[] = scoreData.map((game, index) => ({
        game: index + 1,
        player1Score: game.player1Score || game.player_1_score || 0,
        player2Score: game.player2Score || game.player_2_score || 0,
        completed: true,
      }));
      // No per-game status here: a retired match's last game is its unfinished one when it is
      // not a valid completed game under the match's format.
      const last = games[games.length - 1];
      if (
        last &&
        isRetiredMatch(match) &&
        isUnfinishedLastGame(
          { player1: last.player1Score, player2: last.player2Score },
          last.game,
          match.event_match_schedule?.match ?? {},
        )
      ) {
        last.completed = false;
      }
      return games;
    }

    // Handle app_state format (a finalize-time copy of the scorer blob): completed games
    // through the shared resolver; the current game only as a retirement's unfinished game.
    const resolved = tryResolveAppState(scoreData);
    if (resolved) {
      const games: PlayedGameScore[] = resolved.games
        .filter((g) => g.completed)
        .map((g) => ({
          game: g.game_number,
          player1Score: g.player_1_score,
          player2Score: g.player_2_score,
          completed: true,
        }));
      const current = resolved.current_game;
      if (isRetiredMatch(match) && current && current.player_1_score + current.player_2_score > 0) {
        games.push({
          game: current.game_number,
          player1Score: current.player_1_score,
          player2Score: current.player_2_score,
          completed: false,
        });
      }
      return games;
    }
  } catch (error) {
    console.warn('Failed to parse game scores:', error);
  }

  return [];
}

/** The completed games of a match: a retired match's unfinished game is never a game. */
export function parseGameScores(match: DatabaseMatch): GameScore[] {
  return parsePlayedGameScores(match)
    .filter((g) => g.completed)
    .map(({ game, player1Score, player2Score }) => ({ game, player1Score, player2Score }));
}

function sortStandings(
  standings: PlayerStanding[],
  tiebreakerOrder: ('games_ratio' | 'points_ratio')[] = ['games_ratio', 'points_ratio'],
): void {
  standings.sort((a, b) => {
    // 1. Primary ranking: Points (always first)
    if (a.points !== b.points) return b.points - a.points;

    // 2. Apply tiebreakers in configured order
    for (const tiebreaker of tiebreakerOrder) {
      const result = applyTiebreaker(a, b, tiebreaker);
      if (result !== 0) return result;
    }

    // 3. Final fallback: Game points percentage
    return b.gamePointsPercentage - a.gamePointsPercentage;
  });

  // Set positions
  standings.forEach((standing, index) => {
    standing.position = index + 1;
  });
}

function applyTiebreaker(
  a: PlayerStanding,
  b: PlayerStanding,
  tiebreaker: 'games_ratio' | 'points_ratio',
): number {
  switch (tiebreaker) {
    case 'games_ratio': {
      // gamesWon / gamesLost ratio
      // Handle division by zero: if gamesLost is 0, use Infinity (or very large number)
      const aRatio = a.gamesLost === 0 ? (a.gamesWon > 0 ? Infinity : 0) : a.gamesWon / a.gamesLost;
      const bRatio = b.gamesLost === 0 ? (b.gamesWon > 0 ? Infinity : 0) : b.gamesWon / b.gamesLost;

      // Higher ratio wins
      if (aRatio === bRatio) return 0;
      if (aRatio === Infinity && bRatio !== Infinity) return -1;
      if (bRatio === Infinity && aRatio !== Infinity) return 1;
      return bRatio - aRatio;
    }

    case 'points_ratio': {
      // gamePointsFor / gamePointsAgainst ratio
      // Handle division by zero: if gamePointsAgainst is 0, use Infinity (or very large number)
      const aRatio =
        a.gamePointsAgainst === 0
          ? a.gamePointsFor > 0
            ? Infinity
            : 0
          : a.gamePointsFor / a.gamePointsAgainst;
      const bRatio =
        b.gamePointsAgainst === 0
          ? b.gamePointsFor > 0
            ? Infinity
            : 0
          : b.gamePointsFor / b.gamePointsAgainst;

      // Higher ratio wins
      if (aRatio === bRatio) return 0;
      if (aRatio === Infinity && bRatio !== Infinity) return -1;
      if (bRatio === Infinity && aRatio !== Infinity) return 1;
      return bRatio - aRatio;
    }

    default:
      return 0;
  }
}
